import React, { useState } from 'react';
import { DataTable } from '../components/DataTable';
import { WizardForm } from '../components/WizardForm';
import { StatusBadge } from '../components/StatusBadge';
import { UserIcon, PhoneIcon, MapPinIcon, BriefcaseIcon, XIcon } from 'lucide-react';
// Mock member data for displaying selected member info
const memberData: Record<string, {
  name: string;
  nid: string;
  phone: string;
  village: string;
  profession: string;
  photo: string;
  age: number;
  group: string;
}> = {
  'Abdur Rahman': {
    name: 'Abdur Rahman',
    nid: '1234567890123',
    phone: '+880 1711-234567',
    village: 'Munshiganj',
    profession: 'Jele (Fisher)',
    photo: '',
    age: 42,
    group: 'CPG - Sundarban Patrol'
  },
  'Jalal Uddin': {
    name: 'Jalal Uddin',
    nid: '2345678901234',
    phone: '+880 1812-345678',
    village: 'Gabura',
    profession: 'Moual (Honey Collector)',
    photo: '',
    age: 38,
    group: 'VTRT - Tiger Watch'
  },
  'Rina Begum': {
    name: 'Rina Begum',
    nid: '3456789012345',
    phone: '+880 1913-456789',
    village: 'Shyamnagar',
    profession: 'Bawali (Wood Collector)',
    photo: '',
    age: 35,
    group: 'DCT - Dolphin Team'
  }
};
export function ConservationManagement() {
  const [activeTab, setActiveTab] = useState<'list' | 'add' | 'approval'>('list');
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
  const [formData, setFormData] = useState({
    conservationGroupType: '',
    groupName: '',
    division: '',
    range: '',
    station: '',
    formationDate: '',
    status: 'Active',
    organization: '',
    monitorName: '',
    guideSelection: '',
    memberSelection: '',
    personnelGroup: '',
    vesselSelection: '',
    proposalDoc: '',
    mapRouteDoc: '',
    authImages: ''
  });
  const stationsByRange: Record<string, string[]> = {
    'Satkhira Range': ['Burigoalini Station', 'Kaikhali Station', 'Dobeki Station'],
    'Khulna Range': ['Nalian Station', 'Patkosta Station', 'Karamjal Station'],
    'Chandpai Range': ['Harbaria Station', 'Joymoni Station', 'Tamulbunia Station'],
    'Sarankhola Range': ['Supati Station', 'Sharankhola Station', 'Kotka Station']
  };
  const conservations = [{
    name: 'Tiger Monitoring Zone A',
    org: 'Sundarban Fisheries Ltd',
    monitor: 'Rahim Uddin',
    status: 'active' as const,
    startDate: '2024-01-10',
    category: 'Tiger Monitoring'
  }, {
    name: 'Dolphin Protection Drive',
    org: 'Bay of Bengal Trading Co.',
    monitor: 'Karim Sheikh',
    status: 'active' as const,
    startDate: '2024-02-15',
    category: 'Dolphin Protection'
  }, {
    name: 'Sundarban Patrol Route B',
    org: 'Khulna Honey Producers',
    monitor: 'Fatema Begum',
    status: 'completed' as const,
    startDate: '2024-01-01',
    category: 'Patrol'
  }, {
    name: 'Eco Tour - Mangrove Trail',
    org: 'Mongla Port Services',
    monitor: 'Abdul Hakim',
    status: 'active' as const,
    startDate: '2024-03-05',
    category: 'Tour'
  }, {
    name: 'Biodiversity Research 2024',
    org: 'Satkhira Wood Industries',
    monitor: 'Jamal Hossain',
    status: 'pending' as const,
    startDate: '2024-04-01',
    category: 'Research'
  }, {
    name: 'Anti-Poaching Patrol C',
    org: 'Sundarban Fisheries Ltd',
    monitor: 'Nasreen Akter',
    status: 'cancelled' as const,
    startDate: '2024-02-20',
    category: 'Patrol'
  }];
  const pendingApprovals = conservations.filter(c => c.status === 'pending');
  const columns = [{
    key: 'name',
    label: 'Conservation Name'
  }, {
    key: 'org',
    label: 'Organization'
  }, {
    key: 'monitor',
    label: 'Monitor Name'
  }, {
    key: 'startDate',
    label: 'Start Date'
  }, {
    key: 'status',
    label: 'Status',
    render: (value: string) => <StatusBadge status={value as any} data-id="element-452" />
  }];
  const filters = [{
    key: 'category',
    label: 'Category',
    options: ['Tiger Monitoring', 'Dolphin Protection', 'Patrol', 'Tour', 'Research']
  }, {
    key: 'status',
    label: 'Status',
    options: ['Active', 'Completed', 'Cancelled', 'Pending']
  }];
  const wizardSteps = [{
    title: 'Conservation Info',
    description: 'Enter basic conservation details'
  }, {
    title: 'Resource Mapping',
    description: 'Allocate guides, personnel, and vessels'
  }, {
    title: 'Organizational Tagging',
    description: 'Assign organization and monitors'
  }, {
    title: 'Attachments',
    description: 'Upload required documents'
  }, {
    title: 'Review & Submit',
    description: 'Review all information before submitting'
  }];
  return <div className="space-y-6" data-id="element-453">
      <div data-id="element-454">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-455">
          Conservation Management
        </h1>
        <p className="text-sm text-gray-600 mt-1" data-id="element-456">
          Manage conservation projects, patrols, and eco-tours
        </p>
      </div>

      <div className="border-b border-gray-200" data-id="element-457">
        <div className="flex gap-4" data-id="element-458">
          <button onClick={() => setActiveTab('list')} className={`px-4 py-2 border-b-2 font-medium text-sm transition-colors ${activeTab === 'list' ? 'border-[#1B5E20] text-[#1B5E20]' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-459">
            Conservation List
          </button>
          <button onClick={() => setActiveTab('add')} className={`px-4 py-2 border-b-2 font-medium text-sm transition-colors ${activeTab === 'add' ? 'border-[#1B5E20] text-[#1B5E20]' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-460">
            Add Conservation
          </button>
          <button onClick={() => setActiveTab('approval')} className={`px-4 py-2 border-b-2 font-medium text-sm transition-colors ${activeTab === 'approval' ? 'border-[#1B5E20] text-[#1B5E20]' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-461">
            Approval Queue ({pendingApprovals.length})
          </button>
        </div>
      </div>

      {activeTab === 'list' && <DataTable columns={columns} data={conservations} filters={filters} onView={row => console.log('View', row)} onEdit={row => console.log('Edit', row)} onDelete={row => console.log('Delete', row)} searchPlaceholder="Search conservations..." data-id="element-462" />}

      {activeTab === 'approval' && <DataTable columns={columns} data={pendingApprovals} onView={row => console.log('View', row)} onApprove={row => alert(`Approved: ${row.name}`)} searchPlaceholder="Search pending approvals..." data-id="element-463" />}

      {activeTab === 'add' && <WizardForm steps={wizardSteps} currentStep={currentStep} onStepChange={setCurrentStep} onSubmit={() => {
      if (!formData.conservationGroupType || !formData.groupName || !formData.division) {
        alert('Please fill in all required fields.');
        return;
      }
      alert(`Conservation Group "${formData.groupName}" submitted for approval!`);
      setCurrentStep(0);
      setActiveTab('approval');
      setSelectedMembers([]);
      setFormData({
        conservationGroupType: '',
        groupName: '',
        division: '',
        range: '',
        station: '',
        formationDate: '',
        status: 'Active',
        organization: '',
        monitorName: '',
        guideSelection: '',
        memberSelection: '',
        personnelGroup: '',
        vesselSelection: '',
        proposalDoc: '',
        mapRouteDoc: '',
        authImages: ''
      });
    }} data-id="element-464">
          {currentStep === 0 && <div className="space-y-4" data-id="element-465">
              <div className="grid grid-cols-2 gap-4" data-id="element-466">
                <div data-id="element-467">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-468">
                    Conservation Group Type *
                  </label>
                  <select required value={formData.conservationGroupType} onChange={e => setFormData({
              ...formData,
              conservationGroupType: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-469">
                    <option value="" data-id="element-470">Select Group Type</option>
                    <option value="CPG" data-id="element-471">CPG (Community Patrol Group)</option>
                    <option value="VTRT" data-id="element-472">
                      VTRT (Village Tiger Response Team)
                    </option>
                    <option value="DCT" data-id="element-473">DCT (Dolphin Conservation Team)</option>
                  </select>
                </div>
                <div data-id="element-474">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-475">
                    Group Name *
                  </label>
                  <input type="text" required maxLength={150} value={formData.groupName} onChange={e => setFormData({
              ...formData,
              groupName: e.target.value
            })} placeholder="Max 150 characters" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-476" />
                  <p className="text-xs text-gray-400 mt-1" data-id="element-477">
                    {formData.groupName.length}/150
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4" data-id="element-478">
                <div data-id="element-479">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-480">
                    Division *
                  </label>
                  <select required value={formData.division} onChange={e => setFormData({
              ...formData,
              division: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-481">
                    <option value="" data-id="element-482">Select Division</option>
                    <option value="Khulna" data-id="element-483">Khulna</option>
                    <option value="Satkhira" data-id="element-484">Satkhira</option>
                    <option value="Bagerhat" data-id="element-485">Bagerhat</option>
                  </select>
                </div>
                <div data-id="element-486">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-487">
                    Range
                  </label>
                  <select value={formData.range} onChange={e => setFormData({
              ...formData,
              range: e.target.value,
              station: ''
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-488">
                    <option value="" data-id="element-489">Select Range</option>
                    <option value="Satkhira Range" data-id="element-490">Satkhira Range</option>
                    <option value="Khulna Range" data-id="element-491">Khulna Range</option>
                    <option value="Chandpai Range" data-id="element-492">Chandpai Range</option>
                    <option value="Sarankhola Range" data-id="element-493">Sarankhola Range</option>
                  </select>
                </div>
                <div data-id="element-494">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-495">
                    Station
                  </label>
                  <select value={formData.station} onChange={e => setFormData({
              ...formData,
              station: e.target.value
            })} disabled={!formData.range} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20] disabled:bg-gray-100 disabled:text-gray-400" data-id="element-496">
                    <option value="" data-id="element-497">
                      {formData.range ? 'Select Station' : 'Select Range first'}
                    </option>
                    {formData.range && stationsByRange[formData.range]?.map(station => <option key={station} value={station} data-id="element-498">
                          {station}
                        </option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4" data-id="element-499">
                <div data-id="element-500">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-501">
                    Formation Date
                  </label>
                  <input type="date" value={formData.formationDate} onChange={e => setFormData({
              ...formData,
              formationDate: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-502" />
                </div>
                <div data-id="element-503">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-504">
                    Status
                  </label>
                  <div className="flex items-center gap-4 mt-2" data-id="element-505">
                    <label className="flex items-center gap-2 cursor-pointer" data-id="element-506">
                      <input type="radio" name="conservationStatus" value="Active" checked={formData.status === 'Active'} onChange={e => setFormData({
                  ...formData,
                  status: e.target.value
                })} className="w-4 h-4 text-[#1B5E20] focus:ring-[#1B5E20]" data-id="element-507" />
                      <span className="text-sm text-gray-700" data-id="element-508">Active</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer" data-id="element-509">
                      <input type="radio" name="conservationStatus" value="Inactive" checked={formData.status === 'Inactive'} onChange={e => setFormData({
                  ...formData,
                  status: e.target.value
                })} className="w-4 h-4 text-[#1B5E20] focus:ring-[#1B5E20]" data-id="element-510" />
                      <span className="text-sm text-gray-700" data-id="element-511">Inactive</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>}

          {currentStep === 1 && <div className="space-y-4" data-id="element-512">
              <div data-id="element-513">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-514">
                  Member Selection
                </label>
                <div className="border border-gray-300 rounded-lg p-3 space-y-2 max-h-48 overflow-y-auto" data-id="element-515">
                  {Object.keys(memberData).map(memberName => <label key={memberName} className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${selectedMembers.includes(memberName) ? 'bg-green-50 border border-green-200' : 'hover:bg-gray-50 border border-transparent'}`} data-id="element-516">
                      <input type="checkbox" checked={selectedMembers.includes(memberName)} onChange={e => {
                if (e.target.checked) {
                  setSelectedMembers([...selectedMembers, memberName]);
                } else {
                  setSelectedMembers(selectedMembers.filter(m => m !== memberName));
                }
              }} className="w-4 h-4 text-[#1B5E20] border-gray-300 rounded focus:ring-[#1B5E20]" data-id="element-517" />
                      <div className="w-8 h-8 bg-[#1B5E20]/10 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-518">
                        <UserIcon className="w-4 h-4 text-[#1B5E20]" data-id="element-519" />
                      </div>
                      <div className="flex-1" data-id="element-520">
                        <span className="text-sm font-medium text-gray-900" data-id="element-521">
                          {memberName}
                        </span>
                        <span className="text-xs text-gray-500 ml-2" data-id="element-522">
                          {memberData[memberName].profession} ·{' '}
                          {memberData[memberName].village}
                        </span>
                      </div>
                    </label>)}
                </div>
                {selectedMembers.length > 0 && <p className="text-xs text-[#1B5E20] mt-1 font-medium" data-id="element-523">
                    {selectedMembers.length} member
                    {selectedMembers.length > 1 ? 's' : ''} selected
                  </p>}
              </div>

              {/* Selected Members Information */}
              {selectedMembers.length > 0 && <div className="space-y-3" data-id="element-524">
                  <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2" data-id="element-525">
                    <UserIcon className="w-4 h-4 text-[#1B5E20]" data-id="element-526" />
                    Selected Members Information
                  </h4>
                  {selectedMembers.map(memberName => {
            const member = memberData[memberName];
            if (!member) return null;
            return <div key={memberName} className="bg-green-50 border border-green-200 rounded-lg p-4" data-id="element-527">
                        <div className="flex items-start gap-4" data-id="element-528">
                          <div className="w-12 h-12 bg-[#1B5E20]/10 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-529">
                            <UserIcon className="w-6 h-6 text-[#1B5E20]" data-id="element-530" />
                          </div>
                          <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm flex-1" data-id="element-531">
                            <div data-id="element-532">
                              <span className="text-gray-500" data-id="element-533">Name:</span>{' '}
                              <span className="font-medium text-gray-900" data-id="element-534">
                                {member.name}
                              </span>
                            </div>
                            <div data-id="element-535">
                              <span className="text-gray-500" data-id="element-536">NID:</span>{' '}
                              <span className="font-medium text-gray-900" data-id="element-537">
                                {member.nid}
                              </span>
                            </div>
                            <div data-id="element-538">
                              <span className="text-gray-500" data-id="element-539">Phone:</span>{' '}
                              <span className="font-medium text-gray-900" data-id="element-540">
                                {member.phone}
                              </span>
                            </div>
                            <div data-id="element-541">
                              <span className="text-gray-500" data-id="element-542">Village:</span>{' '}
                              <span className="font-medium text-gray-900" data-id="element-543">
                                {member.village}
                              </span>
                            </div>
                            <div data-id="element-544">
                              <span className="text-gray-500" data-id="element-545">Profession:</span>{' '}
                              <span className="font-medium text-gray-900" data-id="element-546">
                                {member.profession}
                              </span>
                            </div>
                            <div data-id="element-547">
                              <span className="text-gray-500" data-id="element-548">Age:</span>{' '}
                              <span className="font-medium text-gray-900" data-id="element-549">
                                {member.age} years
                              </span>
                            </div>
                            <div className="col-span-2" data-id="element-550">
                              <span className="text-gray-500" data-id="element-551">Group:</span>{' '}
                              <span className="font-medium text-gray-900" data-id="element-552">
                                {member.group}
                              </span>
                            </div>
                          </div>
                          <button type="button" onClick={() => setSelectedMembers(selectedMembers.filter(m => m !== memberName))} className="text-gray-400 hover:text-red-500 transition-colors flex-shrink-0" title="Remove member" data-id="element-553">
                            <XIcon className="w-4 h-4" data-id="element-554" />
                          </button>
                        </div>
                      </div>;
          })}
                </div>}
            </div>}

          {currentStep === 2 && <div className="space-y-4" data-id="element-555">
              <div className="grid grid-cols-2 gap-4" data-id="element-556">
                <div data-id="element-557">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-558">
                    Select Organization *
                  </label>
                  <select required value={formData.organization} onChange={e => setFormData({
              ...formData,
              organization: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-559">
                    <option value="" data-id="element-560">Select Organization</option>
                    <option value="Sundarban Fisheries Ltd" data-id="element-561">
                      Sundarban Fisheries Ltd
                    </option>
                    <option value="Bay of Bengal Trading Co." data-id="element-562">
                      Bay of Bengal Trading Co.
                    </option>
                    <option value="Khulna Honey Producers" data-id="element-563">
                      Khulna Honey Producers
                    </option>
                    <option value="Mongla Port Services" data-id="element-564">
                      Mongla Port Services
                    </option>
                    <option value="Satkhira Wood Industries" data-id="element-565">
                      Satkhira Wood Industries
                    </option>
                  </select>
                </div>
                <div data-id="element-566">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-567">
                    Guide Selection
                  </label>
                  <select value={formData.guideSelection} onChange={e => setFormData({
              ...formData,
              guideSelection: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-568">
                    <option value="" data-id="element-569">Select Guide/Lead</option>
                    <option value="Mostafa Kamal" data-id="element-570">Mostafa Kamal</option>
                    <option value="Rafiq Ahmed" data-id="element-571">Rafiq Ahmed</option>
                    <option value="Salma Khatun" data-id="element-572">Salma Khatun</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4" data-id="element-573">
                <div data-id="element-574">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-575">
                    Monitor Assignment (User/Admin)
                  </label>
                  <select value={formData.monitorName} onChange={e => setFormData({
              ...formData,
              monitorName: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-576">
                    <option value="" data-id="element-577">Select Monitor</option>
                    <option value="Admin HQ" data-id="element-578">Admin HQ</option>
                    <option value="Rahim Uddin" data-id="element-579">Rahim Uddin</option>
                    <option value="Karim Sheikh" data-id="element-580">Karim Sheikh</option>
                    <option value="Fatema Begum" data-id="element-581">Fatema Begum</option>
                  </select>
                </div>
                <div data-id="element-582">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-583">
                    Vessel Selection
                  </label>
                  <select value={formData.vesselSelection} onChange={e => setFormData({
              ...formData,
              vesselSelection: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-584">
                    <option value="" data-id="element-585">Select Vessel (Optional)</option>
                    <option value="MV Sundarban-1" data-id="element-586">MV Sundarban-1</option>
                    <option value="FV Padma-3" data-id="element-587">FV Padma-3</option>
                    <option value="BT Meghna-7" data-id="element-588">BT Meghna-7</option>
                  </select>
                </div>
              </div>
            </div>}

          {currentStep === 3 && <div className="space-y-4" data-id="element-589">
              <div data-id="element-590">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-591">
                  Conservation Proposal
                </label>
                <input type="file" accept="application/pdf" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-592" />
              </div>
              <div data-id="element-593">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-594">
                  Map/Route PDF
                </label>
                <input type="file" accept="application/pdf" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-595" />
              </div>
              <div data-id="element-596">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-597">
                  Authorization Images
                </label>
                <input type="file" accept="image/*" multiple className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-598" />
              </div>
            </div>}

          {currentStep === 4 && <div className="space-y-6" data-id="element-599">
              <div className="bg-gray-50 rounded-lg p-4" data-id="element-600">
                <div className="flex items-center justify-between mb-3" data-id="element-601">
                  <h4 className="font-semibold text-gray-900" data-id="element-602">
                    Conservation Info
                  </h4>
                  <button onClick={() => setCurrentStep(0)} className="text-sm text-[#1B5E20] hover:underline" data-id="element-603">
                    Edit
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm" data-id="element-604">
                  <div data-id="element-605">
                    <span className="text-gray-600" data-id="element-606">Group Type:</span>{' '}
                    <span className="font-medium" data-id="element-607">
                      {formData.conservationGroupType || 'Not provided'}
                    </span>
                  </div>
                  <div data-id="element-608">
                    <span className="text-gray-600" data-id="element-609">Group Name:</span>{' '}
                    <span className="font-medium" data-id="element-610">
                      {formData.groupName || 'Not provided'}
                    </span>
                  </div>
                  <div data-id="element-611">
                    <span className="text-gray-600" data-id="element-612">Division:</span>{' '}
                    <span className="font-medium" data-id="element-613">
                      {formData.division || 'Not provided'}
                    </span>
                  </div>
                  <div data-id="element-614">
                    <span className="text-gray-600" data-id="element-615">Range:</span>{' '}
                    <span className="font-medium" data-id="element-616">
                      {formData.range || 'Not provided'}
                    </span>
                  </div>
                  <div data-id="element-617">
                    <span className="text-gray-600" data-id="element-618">Station:</span>{' '}
                    <span className="font-medium" data-id="element-619">
                      {formData.station || 'Not provided'}
                    </span>
                  </div>
                  <div data-id="element-620">
                    <span className="text-gray-600" data-id="element-621">Formation Date:</span>{' '}
                    <span className="font-medium" data-id="element-622">
                      {formData.formationDate || 'Not provided'}
                    </span>
                  </div>
                  <div data-id="element-623">
                    <span className="text-gray-600" data-id="element-624">Status:</span>{' '}
                    <span className={`font-medium ${formData.status === 'Active' ? 'text-green-700' : 'text-gray-500'}`} data-id="element-625">
                      {formData.status}
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4" data-id="element-626">
                <div className="flex items-center justify-between mb-3" data-id="element-627">
                  <h4 className="font-semibold text-gray-900" data-id="element-628">
                    Resource Mapping
                  </h4>
                  <button onClick={() => setCurrentStep(1)} className="text-sm text-[#1B5E20] hover:underline" data-id="element-629">
                    Edit
                  </button>
                </div>
                <div className="text-sm" data-id="element-630">
                  <span className="text-gray-600" data-id="element-631">Members:</span>{' '}
                  <span className="font-medium" data-id="element-632">
                    {selectedMembers.length > 0 ? selectedMembers.join(', ') : 'Not provided'}
                  </span>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4" data-id="element-633">
                <div className="flex items-center justify-between mb-3" data-id="element-634">
                  <h4 className="font-semibold text-gray-900" data-id="element-635">
                    Organizational Tagging
                  </h4>
                  <button onClick={() => setCurrentStep(2)} className="text-sm text-[#1B5E20] hover:underline" data-id="element-636">
                    Edit
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm" data-id="element-637">
                  <div data-id="element-638">
                    <span className="text-gray-600" data-id="element-639">Organization:</span>{' '}
                    <span className="font-medium" data-id="element-640">
                      {formData.organization || 'Not provided'}
                    </span>
                  </div>
                  <div data-id="element-641">
                    <span className="text-gray-600" data-id="element-642">Guide:</span>{' '}
                    <span className="font-medium" data-id="element-643">
                      {formData.guideSelection || 'Not provided'}
                    </span>
                  </div>
                  <div data-id="element-644">
                    <span className="text-gray-600" data-id="element-645">Monitor:</span>{' '}
                    <span className="font-medium" data-id="element-646">
                      {formData.monitorName || 'Not provided'}
                    </span>
                  </div>
                  <div data-id="element-647">
                    <span className="text-gray-600" data-id="element-648">Vessel:</span>{' '}
                    <span className="font-medium" data-id="element-649">
                      {formData.vesselSelection || 'Not provided'}
                    </span>
                  </div>
                </div>
              </div>
            </div>}
        </WizardForm>}
    </div>;
}